import React from "react";
import Hero from "@/components/Hero";
import FAQ from "@/components/FAQ";
import Marquee from "@/components/Marquee";
import { Manifesto, DualCallout } from "@/components/Editorial";
import { Reveal } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { MapPin, Phone, Clock, MessageCircle, Facebook, Instagram } from "lucide-react";
import { HERO_IMG, SITE } from "@/data/site";

export default function Contatti() {
    return (
        <>
            <Hero
                eyebrow="Centro Estetico a Livorno"
                title={["Contatta", "Spazio Isa."]}
                subtitle="Siamo a tua disposizione per informazioni, consulenze e appuntamenti. Ti aspettiamo nel nostro centro estetico in Via Minghi 30, quartiere Coteto."
                subtitleMobile="Siamo a tua disposizione per informazioni e appuntamenti."
                imgDesktop={HERO_IMG.contatti.d}
                imgMobile={HERO_IMG.contatti.m}
                primaryCta={{ label: "Chatta su WhatsApp", href: SITE.whatsappHref }}
                secondaryCta={{ label: "I nostri servizi", href: "/estetica" }}
            />

            <div className="bg-ivory pt-8 pb-4"></div>

            {/* Contact info grid */}
            <section className="py-24 md:py-32 bg-ivory">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <SectionLabel number="01" text="Come contattarci" />
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="mt-5 font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                    Ti aspettiamo nel nostro centro.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-7">
                            <Reveal delay={0.15}>
                                <p className="text-ink-soft text-[16.5px] leading-relaxed max-w-xl">
                                    Hai bisogno di maggiori informazioni sui
                                    nostri trattamenti oppure desideri
                                    prenotare un appuntamento? Puoi contattarci
                                    telefonicamente oppure venirci a trovare
                                    direttamente nel nostro centro estetico.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        <ContactCard
                            testId="contact-address"
                            icon={<MapPin size={20} />}
                            label="Indirizzo"
                            title={SITE.address.street}
                            sub={`${SITE.address.cap} ${SITE.address.city} · ${SITE.address.district}`}
                            bg="bg-rose-powder/60"
                        />
                        <ContactCard
                            testId="contact-phone"
                            icon={<Phone size={20} />}
                            label="Telefono"
                            title={SITE.phone}
                            sub="Chiamaci per fissare un appuntamento"
                            href={SITE.phoneHref}
                            bg="bg-ivory-warm"
                        />
                        <ContactCard
                            testId="contact-mobile"
                            icon={<MessageCircle size={20} />}
                            label="WhatsApp"
                            title={SITE.mobile}
                            sub="Chatta con noi su WhatsApp"
                            href={SITE.whatsappHref}
                            external
                            bg="bg-blush/60"
                        />
                        <ContactCard
                            testId="contact-hours"
                            icon={<Clock size={20} />}
                            label="Orari"
                            title="Mar–Ven · 09–18:30"
                            sub={"Sab · 09–13\nLun · Chiuso\nDom · Chiuso"}
                            bg="bg-sage/30"
                        />
                    </div>

                    {/* Social links */}
                    <div className="mt-16 md:mt-20">
                        <div className="mb-8">
                            <SectionLabel text="Seguici sui social" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
                            <a
                                data-testid="social-facebook"
                                href={SITE.facebookUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-5 rounded-[24px] bg-ivory-warm hover:bg-rose-powder/50 p-6 md:p-7 transition-all border border-line hover:border-raspberry"
                            >
                                <span className="w-14 h-14 shrink-0 rounded-full bg-raspberry text-ivory grid place-items-center">
                                    <Facebook size={22} />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <SectionLabel text="Facebook" tone="muted" />
                                    <p className="mt-2 font-display text-xl md:text-2xl text-ink leading-tight truncate group-hover:text-raspberry transition-colors">
                                        @isaspazio
                                    </p>
                                </div>
                            </a>
                            <a
                                data-testid="social-instagram"
                                href={SITE.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-5 rounded-[24px] bg-ivory-warm hover:bg-rose-powder/50 p-6 md:p-7 transition-all border border-line hover:border-raspberry"
                            >
                                <span className="w-14 h-14 shrink-0 rounded-full bg-raspberry text-ivory grid place-items-center">
                                    <Instagram size={22} />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <SectionLabel text="Instagram" tone="muted" />
                                    <p className="mt-2 font-display text-xl md:text-2xl text-ink leading-tight truncate group-hover:text-raspberry transition-colors">
                                        @centro_estetico_spazioisa
                                    </p>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
            <section className="relative py-24 md:py-32 bg-ivory-warm">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <SectionLabel number="02" text="Vieni a trovarci" />
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="mt-5 font-display text-[32px] md:text-[46px] leading-[1.08] text-ink">
                                    Nel cuore del quartiere Coteto.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="mt-6 text-ink-soft text-[16px] leading-relaxed max-w-lg">
                                    Spazio Isa si trova nel quartiere Coteto,
                                    una delle zone più conosciute di Livorno. Il
                                    centro è facilmente raggiungibile e
                                    rappresenta un ambiente accogliente dove
                                    ogni cliente può dedicarsi con tranquillità
                                    alla cura di sé.
                                </p>
                            </Reveal>
                            <Reveal delay={0.3}>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                        `${SITE.address.street}, ${SITE.address.city}`,
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="directions-link"
                                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-raspberry text-ivory px-6 py-3 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-raspberry-deep transition-all"
                                >
                                    Ottieni indicazioni
                                </a>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-7">
                            <Reveal delay={0.1}>
                                <div className="relative overflow-hidden rounded-[28px] shadow-editorial aspect-[4/3]">
                                    <iframe
                                        title="Mappa Spazio Isa"
                                        data-testid="map-embed"
                                        src={SITE.mapEmbed}
                                        className="absolute inset-0 w-full h-full"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <DualCallout
                eyebrow="Prenota"
                title="Prenota il tuo appuntamento."
                subtitle="Prendersi cura di sé significa dedicare tempo al proprio benessere. Puoi contattarci telefonicamente per richiedere informazioni, conoscere la disponibilità o prenotare."
                cards={[
                    {
                        title: "Come prenotare",
                        list: [
                            "chiamando il numero fisso;",
                            "contattandoci sul cellulare;",
                            "richiedendo informazioni sui trattamenti;",
                            "chiedendo una consulenza prima della prenotazione.",
                        ],
                    },
                    {
                        title: "Una consulenza personalizzata",
                        body: "Ogni persona ha esigenze diverse. Prima di scegliere un trattamento o un prodotto siamo sempre disponibili ad ascoltare le tue necessità e a consigliarti la soluzione più adatta.",
                    },
                ]}
            />

            <Manifesto
                number="03"
                eyebrow="Perché Spazio Isa"
                title="Un centro estetico che mette al primo posto l'ascolto."
                body={[
                    "Da oltre 15 anni Spazio Isa è un punto di riferimento a Livorno per chi desidera affidarsi a un centro che mette al primo posto professionalità, esperienza e attenzione alla persona.",
                    "L'ambiente è pensato per offrire un momento di benessere, relax e cura di sé, dove ogni trattamento viene eseguito con precisione e attenzione ai dettagli.",
                    "Scegliere Spazio Isa significa affidarsi a un'estetista che accompagna le proprie clienti con competenza, dalla consulenza iniziale fino alla cura quotidiana.",
                ]}
                accent="bg-rose-powder/40"
            />

            <FAQ
                items={[
                    {
                        q: "Dove si trova Spazio Isa?",
                        a: "In Via A. Minghi 30, nel quartiere Coteto, a Livorno.",
                    },
                    {
                        q: "Come posso prenotare un appuntamento?",
                        a: "Puoi prenotare telefonicamente chiamando il numero fisso oppure contattandoci al cellulare.",
                    },
                    {
                        q: "È necessario prenotare?",
                        a: "Sì, la prenotazione è consigliata per permetterci di dedicare a ogni cliente il tempo necessario.",
                    },
                    {
                        q: "Quali sono gli orari di apertura?",
                        a: "Aperti dal martedì al venerdì con orario continuato 09:00–18:30 e il sabato 09:00–13:00. Chiusi lunedì, sabato pomeriggio e domenica.",
                    },
                    {
                        q: "Posso ricevere una consulenza?",
                        a: "Certamente. Saremo felici di ascoltare le tue esigenze e consigliarti il trattamento o il prodotto più adatto.",
                    },
                    {
                        q: "Quali servizi posso prenotare?",
                        a: "Estetica, pressoterapia, epilazione laser, infrarossi viso, radiofrequenza viso, Gelinium, Nail Art e consulenza sui prodotti professionali.",
                    },
                ]}
            />
        </>
    );
}

function ContactCard({ testId, icon, label, title, sub, href, bg, external }) {
    const Wrapper = href ? "a" : "div";
    const extraProps = external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {};
    return (
        <Reveal>
            <Wrapper
                data-testid={testId}
                href={href}
                {...extraProps}
                className={`block rounded-[24px] ${bg} p-8 h-full transition-all hover:shadow-soft-lg`}
            >
                <span className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/70 grid place-items-center text-raspberry mb-6">
                    {icon}
                </span>
                <SectionLabel text={label} tone="muted" />
                <p className="mt-3 font-display text-2xl md:text-[28px] leading-tight text-ink">
                    {title}
                </p>
                <p className="mt-3 text-ink-soft text-[15px] md:text-[16px] whitespace-pre-line leading-relaxed">
                    {sub}
                </p>
            </Wrapper>
        </Reveal>
    );
}
